import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar, MapPin, Users, GraduationCap, AlertCircle, CheckCircle2,
  Loader2, Wallet, Clock, Building2, ExternalLink, ArrowRight, ShieldCheck, Hourglass
} from 'lucide-react';

const CONSENT_VERSION = 'kompetanseheving-1.0.0';

interface CourseInstance {
  id: string;
  name: string;
  course_code: string | null;
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  description: string | null;
  exam_scheduled_at: string | null;
  max_participants: number | null;
  registration_fee_nok: number;
  registration_status: string;
  show_registered_count: boolean;
  available_slots: number | null;
  registered_count: number | null;
  waitlist_count: number | null;
  self_cancel_deadline_days: number;
  late_cancellation_fee_nok: number;
  late_cancellation_fee_active: boolean;
  no_show_fee_active: boolean;
  study_plan: {
    id: string;
    title: string;
    description?: string | null;
    ects_credits: number;
    verification_url: string | null;
  };
  included_modules?: Array<{
    id: string;
    code: string;
    title: string;
    ects_credits: number | null;
    estimated_hours: number | null;
  }>;
  covers_full_study_plan?: boolean;
  host_organization: string;
}

type Step = 'show' | 'consent' | 'phone' | 'vipps_waiting' | 'success';

export function RegisterInstancePage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [instance, setInstance] = useState<CourseInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('show');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [result, setResult] = useState<{
    waitlist: boolean;
    pending_approval?: boolean;
    approval_status?: string;
    approval_mode?: string;
    waitlist_position?: number;
    self_cancellation_token: string;
    waitlist_withdraw_token?: string;
    payment_required: boolean;
    registration_fee_nok: number;
  } | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!instanceId) return;
    (async () => {
      try {
        const res = await fetch(`/api/v1/course-instances/${instanceId}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Fant ikke kurs');
        }
        setInstance(data.instance);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kunne ikke laste kursinfo');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [instanceId]);

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('no-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString('no-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const startRegistration = () => {
    setStep('consent');
    setError(null);
  };

  const acceptConsent = () => {
    setConsentAccepted(true);
    setStep('phone');
  };

  const startVipps = async () => {
    if (!phoneNumber || phoneNumber.length < 8) {
      setError('Skriv inn et gyldig telefonnummer');
      return;
    }

    setSubmitting(true);
    setStep('vipps_waiting');
    setError(null);

    try {
      const res = await fetch('/api/candidate/auth/vipps/ciba-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke starte Vipps');

      // Start polling
      pollCiba(data.auth_req_id, data.poll_interval || 5);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke starte Vipps');
      setStep('phone');
      setSubmitting(false);
    }
  };

  const pollCiba = (authReqId: string, interval: number) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/candidate/auth/vipps/ciba-status/${authReqId}`);
        const data = await res.json();

        if (data.status === 'pending') return;

        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }

        if (data.status === 'vipps_verified') {
          await completeRegistration(data.session_token);
          return;
        }

        if (data.status === 'rejected' || data.status === 'expired' || data.status === 'error') {
          setError(data.error || 'Vipps-autentisering mislyktes');
          setStep('phone');
          setSubmitting(false);
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    pollingRef.current = setInterval(poll, interval * 1000);
    setTimeout(poll, 1000);
  };

  const completeRegistration = async (sessionToken: string) => {
    try {
      const res = await fetch(`/api/public/register-instance/${instanceId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: sessionToken,
          consent_version: CONSENT_VERSION
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Påmelding mislyktes');
      }
      setResult({
        waitlist: data.waitlist,
        pending_approval: data.pending_approval,
        approval_status: data.approval_status,
        approval_mode: data.approval_mode,
        waitlist_position: data.waitlist_position,
        self_cancellation_token: data.self_cancellation_token,
        waitlist_withdraw_token: data.waitlist_withdraw_token,
        payment_required: data.payment_required,
        registration_fee_nok: data.registration_fee_nok
      });
      setStep('success');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Påmelding mislyktes');
      setStep('phone');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue-700" />
      </div>
    );
  }

  if (error && !instance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-kf-error-200 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-kf-error-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Fant ikke kurset</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <a href="https://kompetanseheving.no" className="text-kf-blue-700 hover:underline inline-flex items-center gap-1">
            Tilbake til kompetanseheving.no <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  if (!instance) return null;

  // Bruker registration_status istedenfor available_slots fordi available_slots er null
  // når show_registered_count=false (vert vil ikke avsløre tall til allmenheten)
  const isFullWithWaitlist = instance.registration_status === 'full';
  const isCancelled = instance.registration_status === 'cancelled';
  const isClosed = instance.registration_status === 'closed';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header — Kompetanseforbundet brand */}
          <div className="bg-gradient-to-r from-kf-blue-800 to-kf-blue-950 px-6 py-5 text-white border-b-4 border-kf-orange-600">
            <h1 className="text-2xl font-bold">{instance.name}</h1>
            <p className="text-kf-blue-100 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {instance.host_organization}
            </p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {instance.description && (
              <div className="text-gray-700 whitespace-pre-line">
                {instance.description}
              </div>
            )}

            {/* Detaljer-grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {instance.start_date && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-kf-blue-700 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Kursstart</div>
                    <div className="font-medium text-gray-900">{formatDate(instance.start_date)}</div>
                    {instance.end_date && (
                      <div className="text-sm text-gray-600">
                        Slutt: {formatDate(instance.end_date)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {instance.exam_scheduled_at && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-kf-orange-600 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Eksamen</div>
                    <div className="font-medium text-gray-900">{formatDateTime(instance.exam_scheduled_at)}</div>
                  </div>
                </div>
              )}

              {instance.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Sted</div>
                    <div className="font-medium text-gray-900">{instance.location}</div>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-kf-success-600 mt-0.5" />
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    {instance.show_registered_count ? 'Påmeldte' : 'Plasser'}
                  </div>
                  <div className="font-medium text-gray-900">
                    {instance.show_registered_count
                      ? `${instance.registered_count ?? 0}${instance.max_participants ? ` / ${instance.max_participants}` : ''}`
                      : (instance.max_participants
                          ? `${instance.max_participants} plasser`
                          : 'Ubegrenset')}
                  </div>
                  {instance.show_registered_count && (instance.waitlist_count ?? 0) > 0 && (
                    <div className="text-sm text-kf-orange-700">
                      {instance.waitlist_count} på venteliste
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Studieplan-info */}
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-4">
              <div className="flex items-start gap-3">
                <GraduationCap className="w-5 h-5 text-kf-blue-800 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-kf-blue-800 uppercase tracking-wide">Godkjent studieplan</div>
                  <div className="font-medium text-gray-900">{instance.study_plan.title}</div>
                  <div className="text-sm text-gray-700 mt-1">
                    {instance.study_plan.ects_credits} kompetansepoeng
                  </div>
                  {instance.study_plan.description && (
                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">
                      {instance.study_plan.description}
                    </p>
                  )}
                  {instance.study_plan.verification_url && (
                    <a
                      href={instance.study_plan.verification_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-kf-blue-700 hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      Se studieplan <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Task #192: Moduler kurset dekker */}
            {Array.isArray(instance.included_modules) && instance.included_modules.length > 0 && (
              <div className="bg-white border border-kf-blue-200 rounded p-4">
                <div className="text-sm font-semibold text-kf-blue-950 mb-2">
                  {instance.covers_full_study_plan
                    ? `Kurset dekker hele studieplanen (${instance.included_modules.length} moduler)`
                    : `Kurset dekker ${instance.included_modules.length} av modulene i studieplanen`}
                </div>
                <ul className="space-y-1.5">
                  {instance.included_modules.map((mod) => (
                    <li key={mod.id} className="text-sm flex items-start gap-2">
                      <span className="text-xs font-mono bg-kf-blue-50 text-kf-blue-800 px-1.5 py-0.5 rounded">
                        {mod.code}
                      </span>
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{mod.title}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {mod.ects_credits ? `${mod.ects_credits} sp` : ''}
                          {mod.estimated_hours ? ` · ${mod.estimated_hours}t` : ''}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                {!instance.covers_full_study_plan && (
                  <p className="text-xs text-gray-600 mt-3">
                    Kompetanseattesten du får etter bestått eksamen reflekterer kun
                    læringsutbyttene i disse modulene.
                  </p>
                )}
              </div>
            )}

            {/* Påmeldingsgebyr */}
            {instance.registration_fee_nok > 0 && (
              <div className="bg-kf-orange-50 border border-kf-orange-200 rounded p-4">
                <div className="flex items-start gap-3">
                  <Wallet className="w-5 h-5 text-kf-orange-700 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-kf-blue-950">
                      Påmeldingsgebyr: {instance.registration_fee_nok.toLocaleString('no-NO')} kr
                    </div>
                    <p className="text-sm text-gray-700 mt-1">
                      Påmeldingsgebyret kreves inn direkte av kursarrangøren ({instance.host_organization}) —
                      betaling via systemet kommer i senere versjoner. Kursavgiften (om annerledes) avtales også
                      direkte med kursarrangør. Påmeldingsgebyret refunderes ikke ved avmelding.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Avbestillings-/no-show-gebyr */}
            {instance.late_cancellation_fee_nok > 0 &&
              (instance.late_cancellation_fee_active || instance.no_show_fee_active) && (
              <div className="bg-kf-orange-50 border border-kf-orange-300 rounded p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-kf-orange-700 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-kf-blue-950">
                      Avbestillings-/no-show-gebyr: {instance.late_cancellation_fee_nok.toLocaleString('no-NO')} kr
                    </div>
                    <ul className="text-sm text-gray-700 mt-1 list-disc list-inside space-y-0.5">
                      {instance.late_cancellation_fee_active && (
                        <li>
                          Hvis du avmelder deg senere enn {instance.self_cancel_deadline_days} dager før kursstart
                        </li>
                      )}
                      {instance.no_show_fee_active && (
                        <li>
                          Hvis du ikke møter til eksamen
                        </li>
                      )}
                    </ul>
                    <p className="text-xs text-gray-600 mt-2">
                      Gebyret kreves inn direkte av kursarrangøren ({instance.host_organization}) —
                      automatisk betaling i systemet kommer i senere versjoner.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CTA-area */}
            {step === 'show' && !isCancelled && !isClosed && (
              <div className="border-t border-gray-200 pt-6">
                {isFullWithWaitlist ? (
                  <button
                    onClick={startRegistration}
                    className="w-full bg-kf-orange-600 hover:bg-kf-orange-700 text-kf-blue-950 font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-colors"
                  >
                    Sett deg på venteliste via Vipps
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={startRegistration}
                    className="w-full bg-kf-orange-600 hover:bg-kf-orange-700 text-kf-blue-950 font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-colors"
                  >
                    Meld deg på via Vipps
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Påmelding krever Vipps-konto. Vi henter navn, mobilnummer og fødselsdato.
                </p>
              </div>
            )}

            {isCancelled && (
              <div className="bg-kf-error-50 border border-kf-error-300 rounded p-4 text-kf-error-800">
                <strong>Avlyst:</strong> Dette kurset er avlyst.
              </div>
            )}

            {isClosed && (
              <div className="bg-gray-100 border border-gray-300 rounded p-4 text-gray-700">
                <strong>Påmelding stengt:</strong> Kontakt kursarrangør for ny dato.
              </div>
            )}

            {/* Samtykke-skjerm (v6.6.1) */}
            {step === 'consent' && (
              <div className="border-t border-gray-200 pt-6 space-y-4">
                <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <ShieldCheck className="w-6 h-6 text-kf-blue-700 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-kf-blue-950 mb-1">
                        Før du melder deg på
                      </h3>
                      <p className="text-sm text-kf-blue-900">
                        Vi ber deg bekrefte hva som skjer med opplysningene dine når du melder deg på.
                      </p>
                    </div>
                  </div>

                  <div className="text-sm text-gray-800 space-y-3 pl-1">
                    <div>
                      <strong className="text-kf-blue-950">Hva vi henter fra Vipps:</strong>
                      <p className="mt-1">Navn, mobilnummer, e-post og fødselsdato. Vi henter <em>ikke</em> fødselsnummer.</p>
                    </div>

                    <div>
                      <strong className="text-kf-blue-950">Hva vi lagrer om deg:</strong>
                      <p className="mt-1">
                        Påmeldingen din lagres hos <strong>{instance.host_organization}</strong> (kursarrangør)
                        via Kompetanseforbundets kvalitetsplattform. Vi bruker dine opplysninger til å administrere
                        kurset og eventuell eksamen, og for å utstede kompetanseattest hvis du fullfører.
                      </p>
                    </div>

                    <div>
                      <strong className="text-kf-blue-950">Endringer i påmeldingen:</strong>
                      <p className="mt-1">
                        Kursarrangør kan i unntakstilfeller måtte gjøre endringer i påmeldingen (f.eks.
                        avlyse kurs, endre datoer, eller trekke tilbake plass ved manglende forutsetninger).
                        Vi tar da kontakt med deg så snart som mulig.
                      </p>
                    </div>

                    <div>
                      <strong className="text-kf-blue-950">Selvavmelding og venteliste:</strong>
                      <p className="mt-1">
                        Du kan selv trekke deg fra ventelisten eller avmelde deg innen fristen via lenke i
                        bekreftelses-e-post. Etter fristen må du kontakte kursarrangør direkte.
                      </p>
                    </div>

                    <div className="pt-1">
                      <a
                        href="https://www.kompetanseattest.no/personvern"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-kf-blue-700 hover:underline text-sm inline-flex items-center gap-1"
                      >
                        Les full personvernerklæring <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={e => setConsentAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-kf-blue-700 focus:ring-kf-blue-500"
                  />
                  <span className="text-sm text-gray-800">
                    Jeg har lest informasjonen over, og samtykker til at mine opplysninger behandles slik som beskrevet
                    for å administrere påmelding, deltakelse og eventuell attestutstedelse.
                  </span>
                </label>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={acceptConsent}
                    disabled={!consentAccepted}
                    className="flex-1 bg-kf-orange-600 hover:bg-kf-orange-700 disabled:bg-gray-300 disabled:text-gray-500 text-kf-blue-950 font-semibold py-3 px-4 rounded-lg shadow-sm transition-colors"
                  >
                    Fortsett til Vipps
                  </button>
                  <button
                    onClick={() => { setConsentAccepted(false); setStep('show'); }}
                    className="text-sm text-gray-600 hover:text-kf-blue-950 px-4 py-2"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            )}

            {/* Telefonnummer-input */}
            {step === 'phone' && (
              <div className="border-t border-gray-200 pt-6 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Mobilnummer</span>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    placeholder="+47 12 34 56 78"
                    className="mt-1 block w-full rounded border-gray-300 px-3 py-2 focus:border-kf-blue-500 focus:ring-kf-blue-500"
                    autoFocus
                  />
                </label>
                {error && (
                  <div className="text-sm text-kf-error-700 bg-kf-error-50 border border-kf-error-200 rounded p-2">
                    {error}
                  </div>
                )}
                <button
                  onClick={startVipps}
                  disabled={submitting || !phoneNumber}
                  className="w-full bg-kf-orange-600 hover:bg-kf-orange-700 disabled:bg-gray-300 disabled:text-gray-500 text-kf-blue-950 font-semibold py-3 px-4 rounded-lg shadow-sm transition-colors"
                >
                  Logg inn med Vipps
                </button>
                <button
                  onClick={() => setStep('show')}
                  className="w-full text-sm text-gray-600 hover:text-kf-blue-950"
                >
                  Avbryt
                </button>
              </div>
            )}

            {/* Vipps-venter */}
            {step === 'vipps_waiting' && (
              <div className="border-t border-gray-200 pt-6 text-center space-y-3">
                <Loader2 className="w-10 h-10 animate-spin text-kf-blue-700 mx-auto" />
                <p className="font-medium text-gray-900">Sjekk Vipps-appen din</p>
                <p className="text-sm text-gray-600">
                  Bekreft innloggingen ved å trykke på riktig tall i Vipps-appen.
                </p>
              </div>
            )}

            {/* Suksess — tre scenarier: bekreftet plass / venter godkjenning / venteliste */}
            {step === 'success' && result && (
              <div className="border-t border-gray-200 pt-6">
                {result.pending_approval ? (
                  // Scenario: manuell modus, venter på arrangør-godkjenning
                  <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-6 text-center space-y-3">
                    <Hourglass className="w-12 h-12 text-kf-blue-700 mx-auto" />
                    <h2 className="text-xl font-bold text-kf-blue-950">
                      Påmelding mottatt — venter på bekreftelse
                    </h2>
                    <p className="text-gray-700">
                      Kursarrangør <strong>{instance.host_organization}</strong> vurderer nå påmeldingen din
                      og gir deg beskjed så snart plassen er bekreftet — vanligvis innen få dager.
                    </p>
                    <p className="text-sm text-gray-600">
                      Vent gjerne med å planlegge oppstart til du har fått bekreftelse på e-post og SMS.
                    </p>
                    <div className="pt-2">
                      <Link
                        to={`/min-pamelding/${result.self_cancellation_token}`}
                        className="text-kf-blue-700 hover:underline inline-flex items-center gap-1 font-medium"
                      >
                        Se status på påmeldingen <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ) : result.waitlist ? (
                  // Scenario: venteliste
                  <div className="bg-kf-warning-50 border border-kf-warning-300 rounded-lg p-6 text-center space-y-3">
                    <Users className="w-12 h-12 text-kf-warning-600 mx-auto" />
                    <h2 className="text-xl font-bold text-kf-blue-950">
                      Du er på venteliste #{result.waitlist_position}
                    </h2>
                    <p className="text-gray-700">
                      Du står som nummer {result.waitlist_position} på ventelisten til kurset. Du vil få
                      beskjed på e-post og SMS hvis du får tildelt plass.
                    </p>
                    {result.waitlist_withdraw_token && (
                      <p className="text-sm text-gray-600">
                        Vi har sendt deg en lenke på e-post der du kan trekke deg fra ventelisten når som helst.
                      </p>
                    )}
                    <div className="pt-2">
                      <Link
                        to={`/min-pamelding/${result.self_cancellation_token}`}
                        className="text-kf-blue-700 hover:underline inline-flex items-center gap-1 font-medium"
                      >
                        Se din påmelding <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  // Scenario: bekreftet plass (auto-godkjent)
                  <div className="bg-kf-success-50 border border-kf-success-200 rounded-lg p-6 text-center space-y-3">
                    <CheckCircle2 className="w-12 h-12 text-kf-success-600 mx-auto" />
                    <h2 className="text-xl font-bold text-kf-blue-950">Plass bekreftet!</h2>
                    <p className="text-gray-700">
                      Bekreftelse er sendt til mobilen og e-posten din. Du vil motta mer informasjon fra
                      kursarrangør før kursstart.
                    </p>
                    <p className="text-xs text-gray-600">
                      Merk: Kursarrangør kan i unntakstilfeller måtte gjøre endringer i påmeldingen. Vi tar
                      i så fall kontakt med deg så snart som mulig.
                    </p>
                    {result.payment_required && (
                      <div className="bg-kf-orange-50 border border-kf-orange-300 rounded p-3 text-sm text-kf-blue-950 text-left">
                        <strong>Påmeldingsgebyr: {result.registration_fee_nok.toLocaleString('no-NO')} kr.</strong>{' '}
                        Kursarrangøren tar kontakt om betaling. Automatisk betaling via systemet kommer i senere versjoner.
                      </div>
                    )}
                    <div className="pt-2">
                      <Link
                        to={`/min-pamelding/${result.self_cancellation_token}`}
                        className="text-kf-blue-700 hover:underline inline-flex items-center gap-1 font-medium"
                      >
                        Se din påmelding <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-500">
          En tjeneste fra <strong className="text-kf-blue-950">Kompetanseforbundet</strong> — kvalitetssikrede kompetanseattester
        </div>
      </div>
    </div>
  );
}
